<template>
  <div class="recharge-page">
    <!-- ICCID 显示区域 -->
    <div class="info-section">
      <van-cell-group inset>
        <van-cell title="ICCID" :value="iccid" />
      </van-cell-group>
    </div>

    <!-- 套餐选择区域 - 优化的三列 Grid 布局 -->
    <div class="package-section">
      <div class="section-title">
        <van-divider dashed>请选择充值套餐</van-divider>
      </div>
      <div class="package-grid">
        <div v-for="pkg in packages" :key="pkg.packageId"
          :class="['package-card', {active: selectedPackage === pkg.packageId}]" @click="selectPackage(pkg.packageId)">
          <div class="package-title">{{ pkg.mobilePkName }}</div>
          <div class="package-price">¥{{ pkg.price }}</div>
          <div class="package-flow">{{ pkg.flow }}M</div>
          <van-icon v-if="selectedPackage === pkg.packageId" name="success" class="check-icon" />
        </div>
      </div>
    </div>

    <!-- 提交按钮区域 - 重新设计 -->
    <div class="submit-area">
      <div class="submit-content">
        <div class="total-amount">
          <van-row>
            <van-col span="24" class="label">实付 <span class="price">¥{{ currentPrice }}</span></van-col>
            <van-col span="24" class="tips">
              <span>{{ validityString }}</span>
            </van-col>
          </van-row>
        </div>
        <van-button type="info" size="large" @click="onRecharge" :disabled="!selectedPackage" class="recharge-button">
          立即充值
        </van-button>
      </div>
    </div>
  </div>
</template>

<script>
import * as api from '@/api/api.js'
import * as packageApi from '@/api/package.js'
import {
  chooseWXPay
} from '@/api/wxShare.js'
import {
  Toast
} from 'vant'

export default {
  name: 'packageAdd', // 确保组件名与文件名一致
  data () {
    return {
      balance: '00.00', // 示例余额 (如果需要，否则可以移除)
      iccid: '',
      orderId: '',
      openid: '',
      czIndex: 0,
      selectedPackage: '', // 当前选中的套餐 value
      packages: []
    }
  },
  computed: {
    // 计算当前选中套餐的价格
    currentPrice () {
      const pkg = this.packages.find(p => p.packageId === this.selectedPackage)
      return pkg ? pkg.price : '0.00'
    },
    validityDescription () {
      const pkg = this.packages.find(p => p.packageId === this.selectedPackage)
      if (!pkg || !pkg.effectiveType || !pkg.effectiveTime) {
        return ''
      }

      const timeValue = parseInt(pkg.effectiveTime, 10)
      if (isNaN(timeValue) || timeValue <= 0) {
        return ''
      }

      switch (pkg.effectiveType) {
        case '01': // 按月份计算
          if (timeValue === 1) {
            return '1个月'
          } else if (timeValue % 12 === 0) {
            const years = timeValue / 12
            return `${years}年`
          } else {
            return `${timeValue}个月`
          }
        case '02': // 按天数计算
          if (timeValue === 1) {
            return '1天'
          } else {
            return `${timeValue}天`
          }
        default:
          console.warn('Unknown effective type for description:', pkg.effectiveType, pkg)
          return ''
      }
    },
    validityString () {
      const pkg = this.packages.find(p => p.packageId === this.selectedPackage)

      if (!this.packages || !Array.isArray(this.packages)) {
        console.warn('Packages data is not ready or not an array.')
        return ''
      }
      if (!this.selectedPackage) {
        return ''
      }
      if (!pkg) {
        console.warn(`Selected package with ID '${this.selectedPackage}' not found in packages list.`)
        return ''
      }
      if (!pkg.effectiveType || !pkg.effectiveTime) {
        console.warn('Selected package is missing effectiveType or effectiveTime:', pkg)
        return ''
      }
      if (pkg.effectiveType !== '01' && pkg.effectiveType !== '02') {
        console.warn('Unknown effective type for selected package:', pkg.effectiveType, pkg)
        return ''
      }
      const timeValue = parseInt(pkg.effectiveTime, 10)
      if (isNaN(timeValue) || timeValue <= 0) {
        console.warn('effectiveTime is not a valid positive number string:', pkg.effectiveTime, pkg)
        return ''
      }

      let durationDescription = ''
      switch (pkg.effectiveType) {
        case '01': // 按月
          if (timeValue === 1) {
            durationDescription = '1个月'
          } else if (timeValue % 12 === 0) {
            const years = timeValue / 12
            durationDescription = `${years}年`
          } else {
            durationDescription = `${timeValue}个月`
          }
          break
        case '02': // 按天
          if (timeValue === 1) {
            durationDescription = '1天'
          } else {
            durationDescription = `${timeValue}天`
          }
          break
      }

      const startDate = new Date()
      let endDate
      try {
        endDate = new Date(startDate)
        if (pkg.effectiveType === '01') {
          endDate.setMonth(endDate.getMonth() + timeValue)
          endDate.setDate(endDate.getDate() - 1)
        } else if (pkg.effectiveType === '02') {
          endDate.setDate(endDate.getDate() + timeValue - 1)
        }

        const formattedStartDate = this.formatDate(startDate)
        const formattedEndDate = this.formatDate(endDate)

        // return `有效期：${durationDescription} （${formattedStartDate} 至 ${formattedEndDate}）`
        return `生效日期：${formattedStartDate} 至 ${formattedEndDate} （共${durationDescription}）`
      } catch (error) {
        console.error('Error calculating validity period:', error, pkg)
        return ''
      }
    }
  },
  methods: {
    getValidityDescriptionForPackage (pkg) {
      if (!pkg || !pkg.effectiveType || !pkg.effectiveTime) {
        return ''
      }

      const timeValue = parseInt(pkg.effectiveTime, 10)
      if (isNaN(timeValue) || timeValue <= 0) {
        return ''
      }

      switch (pkg.effectiveType) {
        case '01': // 按月
          if (timeValue === 1) {
            return '1个月'
          } else if (timeValue % 12 === 0) {
            const years = timeValue / 12
            return `${years}年`
          } else {
            return `${timeValue}个月`
          }
        case '02': // 按天
          if (timeValue === 1) {
            return '1天'
          } else {
            return `${timeValue}天`
          }
        default:
          console.warn('Unknown effective type for description:', pkg.effectiveType, pkg)
          return ''
      }
    },

    getFullValidityStringForPackage (pkg) {
      if (!pkg || !pkg.effectiveType || !pkg.effectiveTime) {
        return ''
      }
      const timeValue = parseInt(pkg.effectiveTime, 10)
      if (isNaN(timeValue) || timeValue <= 0) {
        return ''
      }

      const durationDesc = this.getValidityDescriptionForPackage(pkg)
      if (!durationDesc) return ''

      const startDate = new Date()
      let endDate

      try {
        endDate = new Date(startDate)
        if (pkg.effectiveType === '01') {
          endDate.setMonth(endDate.getMonth() + timeValue)
          endDate.setDate(endDate.getDate() - 1)
        } else if (pkg.effectiveType === '02') {
          endDate.setDate(endDate.getDate() + timeValue - 1)
        }

        const formattedStartDate = this.formatDate(startDate)
        const formattedEndDate = this.formatDate(endDate)

        // 使用方案一的格式
        return `有 效 期：${durationDesc} \n生效日期：${formattedStartDate} 至 ${formattedEndDate}`
      } catch (error) {
        console.error('Error calculating validity period for dialog:', error, pkg)
        return ''
      }
    },
    formatDate (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0') // 月份从0开始，需要+1
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    goBack () {
      this.$router.back() // 返回上一页
    },
    selectPackage (value) {
      this.selectedPackage = value
    },
    /**  充值部分   */
    xfBtn (ress) {
      const that = this
      // 使用 import 进来的 chooseWXPay 函数
      chooseWXPay(ress.timeStamp, ress.nonceStr, ress.packageValue, ress.signType, ress.paySign, ress.appId)
        .then(res => {
          if (res.errMsg === 'chooseWXPay:ok') {
            that.$store.commit('setFlag', 'true')
            that.$router.push({
              name: 'Pay'
            })
          }
        })
        .catch(err => {
          if (err.errMsg === 'chooseWXPay:cancel') return Toast('用户取消支付')
          if (err.errMsg === 'fail') return Toast('支付失败')
        })
    },
    async updates (e) {
      if (e !== 'huoqu') {
        this.$store.commit('setOrder', '')
        this.$store.commit('setFindType', 'liuliang')
        // 使用 import 进来的 api 对象
        const {
          data: res
        } = await packageApi.rechargePackage({
          iccid: this.iccid,
          packageId: this.selectedPackage
        })
        if (res.code !== 0) {
          return Toast.fail(res.msg)
        }
        this.orderId = res.extend.pay.id
        this.$store.commit('setOrder', this.orderId)
      }
      // 使用 import 进来的 api 对象
      const {
        data: ress
      } = await api.payUnifiedOrderV2({
        payId: this.orderId,
        tradeType: 'JSAPI',
        openId: this.openid
      })
      if (ress.code !== 0) {
        return this.$toast(ress.msg) // 使用全局注册的 this.$toast
      }
      if (!ress.extend.payData) {
        return this.$toast('订单获取失败，请稍后重试') // 使用全局注册的 this.$toast
      }
      const payData = ress.extend.payData
      if ('payUrl' in payData) {
        window.location.href = payData.payUrl
        return
      } else if (!ress.appId || !ress.nonceStr || !ress.packageValue || !ress.paySign || !ress.signType || !ress
        .timeStamp) {
        if (this.czIndex > 4) {
          return Toast('支付失败，请稍后重试') // 使用 import 进来的 Toast
        }
        this.czIndex++
        return this.updates('huoqu')
      }
      this.xfBtn(ress)
    },
    onRecharge () {
      const pkg = this.packages.find(p => p.packageId === this.selectedPackage)
      if (!pkg) {
        this.$toast('请选择一个套餐') // 添加一个提示
        return
      }
      const validityInfo = this.getFullValidityStringForPackage(pkg)

      let message = `套餐费用：¥${pkg.price}`
      message += `\n\n包含流量：${pkg.flow}M`
      if (validityInfo) {
        message += `\n\n${validityInfo}`
      }

      this.$dialog.confirm({
        title: '确认充值？',
        message: message, // 使用构建好的消息
        messageAlign: 'left'
      })
        .then(() => {
          this.updates()
        })
        .catch(() => {
          this.$toast('已取消') // 使用全局注册的 this.$toast
        })
    },
    async profit () {
      this.iccid = this.$route.params.iccid
      this.openid = this.$store.state.openId ? this.$store.state.openId : ''
      // 使用 import 进来的 api 对象
      const {
        data: res1
      } = await packageApi.packageList({
        iccid: this.$route.params.iccid
      })
      this.packages = res1.extend.List
      // 默认选中第一个套餐 (移到这里，确保数据加载后执行)
      if (this.packages.length > 0 && !this.selectedPackage) {
        this.selectedPackage = this.packages[0].packageId
      }
    }
  },
  created () {
    this.profit()
  }
}
</script>
<style lang="less" scoped>
  /* --- 移除 CSS 变量，直接使用具体值 (这部分保持不变) --- */

  .recharge-page {
    min-height: 100vh;
    background-color: #f8f9fa;
    /* 直接使用颜色值 */
    padding-bottom: constant(safe-area-inset-bottom);
    padding-bottom: env(safe-area-inset-bottom);
  }

  .info-section {
    margin-top: 16px;
    padding: 0 16px;
  }

  /* --- 强化 /deep/ 并提高优先级 (这部分保持不变) --- */
  .info-section ::v-deep .van-cell-group--inset {
    margin: 0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  .info-section ::v-deep .van-cell {
    padding: 16px;
    background-color: #ffffff !important;
    /* 强制应用背景色 */
    color: #212529 !important;
    /* 强制应用文字色 */
  }

  .info-section ::v-deep .van-cell::after {
    border-bottom-color: #e9ecef !important;
    /* 强制应用边框色 */
  }

  .info-section ::v-deep .van-cell__title {
    color: #6c757d !important;
    /* 强制应用标题色 */
  }

  .info-section ::v-deep .van-cell__value {
    color: #212529 !important;
    /* 强制应用值色 */
    font-weight: 500;
  }

  .package-section {
    margin-top: 24px;
    margin-bottom: calc(80px + 32px);
    /* 为新设计的底部按钮预留更多空间 */
    padding: 0 16px;
  }

  .section-title {
    font-size: 22px;
    color: #6c757d !important;
    /* 强制应用标题色 */
    margin-bottom: 16px;
    padding-left: 8px;
  }

  .package-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    /* 使用具体值 */
  }

  .package-card {
    position: relative;
    background-color: #ffffff;
    /* 直接使用颜色值 */
    border: 1px solid #dee2e6;
    /* 直接使用颜色值 */
    border-radius: 12px;
    padding: 24px 8px;
    /* 使用具体值 */
    text-align: center;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.25, 0.1, 0.25, 1);
    box-sizing: border-box;
    /* 移除 :active 样式，因为可能在移动端不明显或与 vant 冲突 */
    /* &:active {
    background-color: #f1f3f5;
  } */
  }

  .package-card.active {
    border-color: #007bff !important;
    /* 强制应用激活边框色 */
    background-color: #e6f7ff !important;
    /* 强制应用激活背景色 */
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    /* 使用具体值 */

  }

  .package-card.active .package-title {
    color: #007bff !important;
    /* 强制应用激活标题色 */
  }

  .package-card.active .package-price {
    color: #e74c3c !important;
    /* 强制应用激活价格色 */
  }

  .package-title {
    font-size: 24px;
    color: #212529 !important;
    /* 强制应用标题色 */
    margin-bottom: 8px;
    /* 使用具体值 */
    font-weight: 500;
    line-height: 1.4;
  }

  .package-price {
    font-size: 28px;
    /* 显著增大价格字体 */
    color: #57616d !important;
    /* 强制应用默认价格色 */
    font-weight: 600;
    margin-bottom: 4px;
    /* 使用具体值 */
  }

  .package-flow {
    font-size: 32px;
    color: #6c757d !important;
    /* 强制应用流量信息色 */
  }

  .check-icon {
    position: absolute;
    top: 8px;
    /* 使用具体值 */
    right: 8px;
    /* 使用具体值 */
    color: #007bff !important;
    /* 强制应用选中图标色 */
    font-size: 16px;
    background-color: #ffffff !important;
    /* 强制应用图标背景色 */
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #dee2e6 !important;
    /* 强制应用图标边框色 */
  }

  /* ------------------- 重点修改：底部按钮区域样式 (强化版) ------------------- */
  .submit-area {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    /* 增加底部区域的整体内边距，给按钮留出呼吸空间 */
    padding: 16px 16px calc(16px + constant(safe-area-inset-bottom)) 16px;
    padding: 16px 16px calc(16px + env(safe-area-inset-bottom)) 16px;
    z-index: 10;
    background-color: #ffffff !important;
    /* 改为白色背景，更清晰 */
    /* 加强顶部边框，使其更明显 */
    border-top: 1px solid #e9ecef;
    /* 保留轻微的顶部阴影，区分层级 */
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  }

  .submit-content {
    display: flex;
    flex-direction: column;
    /* 改为纵向排列 */
    align-items: stretch;
    /* 子元素拉伸以填充容器宽度 */
    gap: 12px;
    /* 价格和按钮之间的间距 */
  }

  .total-amount {
    display: flex;
    flex-direction: row;
    /* 价格信息本身横向排列 */
    align-items: baseline;
    /* 基线对齐，使 "¥" 和数字更好看 */
    justify-content: left;
    /* 居中显示 */
    gap: 4px;
    /* "实付" 和 "¥XX.XX" 之间的间距 */
  }

  .total-amount .label {
    font-size: 20px;
    /* 稍微增大标签字体 */
    color: #6c757d !important;
  }

  .total-amount .tips {
    font-size: 22px;
    /* 稍微增大标签字体 */
    color: #6c757d !important;
  }

  .total-amount .price {
    font-size: 28px;
    /* 显著增大价格字体 */
    color: #e74c3c !important;
    /* 强制应用价格色 */
    font-weight: 600;
  }

  /* --- 强化 CTA 按钮样式 --- */
  .recharge-button {
    /* 占据父容器全部宽度 */
    width: 100%;

    /* 显著增加按钮尺寸 */
    height: 75px;
    /* 比之前更大 */
    padding: 0 24px;
    /* 左右内边距 */

    /* 更大的圆角 */
    border-radius: 26px;
    /* 高度的一半，形成胶囊状 */

    /* 字体设置 */
    font-size: 32px;
    /* 更大的字体 */
    font-weight: 600;
    /* 更粗的字重 */
    letter-spacing: 1px;
    /* 微小字间距，提升质感 */

    /* 使用 Vant 主色调，但可以考虑加深一点饱和度或亮度以增强对比 */
    /* 注意：这里的颜色依赖于 Vant 的主题色配置 */

    /* 关键：更强的初始阴影 */
    box-shadow: 0 6px 16px -2px rgba(0, 123, 255, 0.4),
      /* 主投影 */
      0 4px 8px -2px rgba(0, 123, 255, 0.2);
    /* 辅助投影，增加深度 */

    /* 过渡效果，使交互更流畅 */
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    /* 确保内容垂直居中 */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* --- 按钮按下/聚焦状态 (强化反馈) --- */
  .recharge-button:active,
  .recharge-button:focus {
    /* 按下时，背景色稍微变深（如果使用的是 Vant 主题色，这会自动适配） */
    /* 或者你可以手动指定一个更深的颜色 */

    /* 模拟按下效果的关键：改变阴影 */
    box-shadow: 0 2px 8px -2px rgba(0, 123, 255, 0.3),
      /* 阴影变得更近、更模糊 */
      0 1px 4px -2px rgba(0, 123, 255, 0.15),
      inset 0 2px 4px rgba(0, 0, 0, 0.1);
    /* 内阴影，增加凹陷感 */

    /* 向下轻微位移，模拟被按下去的感觉 */
    transform: translateY(2px);

    /* 可选：轻微缩小 */
    /* transform: scale(0.98) translateY(1px); */
  }

  /* --- 按钮禁用状态 --- */
  .recharge-button:disabled {
    /* 降低透明度 */
    opacity: 0.5;
    /* 禁用所有变换和阴影效果 */
    transform: none;
    box-shadow: 0 2px 6px -1px rgba(0, 0, 0, 0.1);
    /* 一个很淡的静态阴影 */
    /* 确保没有点击效果 */
    pointer-events: none;
    cursor: not-allowed;
  }

  /* -------------------------------------------------------------------- */

  /* --- 确保 Vant Cell Group 的 inset 样式 --- */
  ::v-deep .van-cell-group--inset {
    margin: 16px;
  }
</style>
